import { create } from 'zustand';
import {
  defaultStudioState,
  type Selection,
  type StudioChannel,
  type StudioMode,
  type StudioState,
} from '../studioModel';
import { docFromFlat, flattenDoc, type ThemeDoc } from '../model/doc';

/**
 * The studio's editing brain: the working document + undo/redo history +
 * ephemeral UI state, in one Zustand store. Server sync (load/autosave) is
 * TanStack Query's job — hooks push loaded docs in via `load()` and read
 * `doc`/`dirty` back out; the store never talks to Supabase itself.
 *
 * History is snapshot-based (docs are ~2 KB — 100 snapshots is pocket change)
 * with coalescing so a color drag or continuous typing collapses into one undo
 * step instead of fifty.
 */

/** What undo/redo restores — the full editable surface. */
interface EditorSnapshot {
  flat: StudioState;
}

const HISTORY_CAP = 100;
const COALESCE_MS = 800;

interface EditorStore {
  /** Flat working view the canvas + inspector bind to. */
  flat: StudioState;
  /** Server document id (null until first save creates the row, or guest mode). */
  themeRowId: string | null;
  themeName: string;
  /** Monotonic counter bumped on every edit — the autosave hook watches this. */
  editSeq: number;
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  saveError: string | null;

  // ephemeral UI
  selection: Selection;
  mode: StudioMode;
  zoom: number;
  pan: { x: number; y: number };

  past: EditorSnapshot[];
  future: EditorSnapshot[];
  lastPatchKey: string | null;
  lastPatchAt: number;

  /** Replace the whole editing surface (loading a doc / switching documents). Clears history. */
  load(init: { flat: StudioState; themeRowId: string | null; themeName: string }): void;
  /** Apply an edit. `coalesceKey` collapses rapid same-control edits into one undo step. */
  patch(p: Partial<StudioState>, coalesceKey?: string): void;
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;

  setThemeRow(id: string | null, name?: string): void;
  markSaving(): void;
  markSaved(): void;
  markSaveError(message: string): void;

  setSelection(s: Selection): void;
  setMode(m: StudioMode): void;
  setZoom(z: number): void;
  setPan(p: { x: number; y: number }): void;

  /** Current doc derived from the working view. */
  doc(): ThemeDoc;
}

export const useEditorStore = create<EditorStore>((set, get) => ({
  flat: defaultStudioState(),
  themeRowId: null,
  themeName: 'Untitled theme',
  editSeq: 0,
  dirty: false,
  saving: false,
  lastSavedAt: null,
  saveError: null,

  selection: 'page',
  mode: 'community',
  zoom: 1,
  pan: { x: 0, y: 0 },

  past: [],
  future: [],
  lastPatchKey: null,
  lastPatchAt: 0,

  load: (init) =>
    set({
      flat: init.flat,
      themeRowId: init.themeRowId,
      themeName: init.themeName,
      editSeq: 0,
      dirty: false,
      saving: false,
      saveError: null,
      past: [],
      future: [],
      lastPatchKey: null,
      lastPatchAt: 0,
      selection: 'page',
      zoom: 1,
      pan: { x: 0, y: 0 },
    }),

  patch: (p, coalesceKey) => {
    const s = get();
    const now = Date.now();
    const coalesce =
      coalesceKey != null && coalesceKey === s.lastPatchKey && now - s.lastPatchAt < COALESCE_MS;
    const past = coalesce ? s.past : [...s.past, { flat: s.flat }].slice(-HISTORY_CAP);
    set({
      flat: { ...s.flat, ...p },
      past,
      future: [],
      editSeq: s.editSeq + 1,
      dirty: true,
      lastPatchKey: coalesceKey ?? null,
      lastPatchAt: now,
    });
  },

  undo: () => {
    const s = get();
    const prev = s.past[s.past.length - 1];
    if (!prev) return;
    set({
      flat: prev.flat,
      past: s.past.slice(0, -1),
      future: [{ flat: s.flat }, ...s.future].slice(0, HISTORY_CAP),
      editSeq: s.editSeq + 1,
      dirty: true,
      lastPatchKey: null,
    });
  },

  redo: () => {
    const s = get();
    const next = s.future[0];
    if (!next) return;
    set({
      flat: next.flat,
      past: [...s.past, { flat: s.flat }].slice(-HISTORY_CAP),
      future: s.future.slice(1),
      editSeq: s.editSeq + 1,
      dirty: true,
      lastPatchKey: null,
    });
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  setThemeRow: (id, name) =>
    set((s) => ({ themeRowId: id, themeName: name ?? s.themeName })),
  markSaving: () => set({ saving: true, saveError: null }),
  markSaved: () => set({ saving: false, dirty: false, lastSavedAt: Date.now(), saveError: null }),
  markSaveError: (message) => set({ saving: false, saveError: message }),

  setSelection: (selection) => set({ selection }),
  setMode: (mode) => set({ mode }),
  setZoom: (zoom) => set({ zoom: Math.min(2, Math.max(0.25, zoom)) }),
  setPan: (pan) => set({ pan }),

  doc: () => docFromFlat(get().flat),
}));

/** Build the flat editing view for a loaded doc + its community context. */
export function flatFromDoc(
  doc: ThemeDoc,
  extras: { communityName?: string; tagline?: string; channels?: StudioChannel[] } = {},
): StudioState {
  const base = defaultStudioState();
  return flattenDoc(doc, {
    communityName: extras.communityName ?? base.communityName,
    tagline: extras.tagline ?? base.tagline,
    channels: extras.channels?.length ? extras.channels : base.channels,
  });
}
